import { Box, Modal, Spinner, TextInput } from "@src/components/elements";
import { useSudoziMutation } from "@src/infrastructure/useSudoziMutation";
import { PostSearchRoute } from "@src/sudozi-schemas/api-route-schemas";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Page, generatePath } from "@src/shared/pages";
import { useEffect } from "react";
import { debounce } from "lodash";
import { z } from "zod";
import {
  PendingIcon,
  SearchIcon,
  ServicesIcon,
  VendorIcon,
} from "@src/components/elements/assets";
import styled from "styled-components/macro";
import { Chip } from "@src/components/elements/Chip";

type SearchResult = z.infer<typeof PostSearchRoute.output>[0];

function pathFor(result: SearchResult) {
  if (result.intakeSubmissionId) {
    return generatePath(Page.INTAKE_REQUEST_DETAILS, {
      intakeSubmissionId: result.intakeSubmissionId,
    });
  }
  if (result.vendorId) {
    return generatePath(Page.VENDOR_DETAILS, {
      vendorId: result.vendorId,
    });
  }
  if (result.serviceId) {
    return generatePath(Page.SERVICE_DETAILS, {
      serviceId: result.serviceId,
    });
  }
  return "#";
}

const StyleRoot = styled.div`
  .result:not(:last-child) {
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    border-bottom: solid 1px var(--N300);
  }
`;

export const UniversalSearchModal = ({ onClose }: { onClose: () => void }) => {
  const postSearch = useSudoziMutation({
    route: PostSearchRoute,
    mutationKey: [PostSearchRoute.path],
  });
  const { handleSubmit, control, watch } = useForm<{ query: string }>();

  const ref = React.createRef<HTMLInputElement>();

  useEffect(() => {
    const subscription = watch(
      debounce(() => {
        void handleSubmit((data) => postSearch.mutate({ body: data }))();
      }, 1000),
    );
    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, postSearch]);

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [ref]);

  const navigate = useNavigate();

  const form = (
    <form
      className="full-width"
      onSubmit={handleSubmit((data) => {
        if (!postSearch.isLoading && (postSearch.data?.length ?? 0) > 0) {
          if (postSearch.data?.[0]) {
            navigate(pathFor(postSearch.data?.[0]));
            onClose();
          }
          return;
        }

        if (!data.query.trim()) {
          return;
        }
        postSearch.mutate({ body: data });
      })}
    >
      <div className="row no-gap">
        {postSearch.isLoading ? (
          <div
            style={{ width: "3rem", height: "1.25rem" }}
            className="row center"
          >
            <Spinner size="small" />
          </div>
        ) : (
          <SearchIcon
            width="3rem"
            height="1.25rem"
            fill="var(--N500)"
            className="no-grow"
          />
        )}
        <Controller
          name="query"
          control={control}
          render={({ field }) => (
            <TextInput
              placeholder="Search vendors and requests"
              ref={ref}
              name="query"
              onChange={field.onChange}
              value={field.value}
              hideError
              className="grow"
            />
          )}
        />
      </div>
    </form>
  );

  return (
    <Modal
      onClose={onClose}
      title={form}
      backgroundColor="var(--N300)"
      hideHeaderDecorations
    >
      <StyleRoot>
        <div>
          {postSearch.isLoading && "Searching..."}
          {!postSearch.data && !postSearch.isLoading && (
            <p>Start typing to search.</p>
          )}
          {!!postSearch.data && postSearch.data.length === 0 && (
            <p>No results found.</p>
          )}
          {!!postSearch.data && postSearch.data.length > 0 && (
            <Box className="margin-bottom">
              {postSearch.data?.map((result) => (
                <div
                  key={`${result.vendorId}${result.intakeSubmissionId}`}
                  className="row result"
                >
                  {!!result.vendorId && (
                    <Chip backgroundColor="var(--N200)">
                      <VendorIcon size={18} /> Vendor
                    </Chip>
                  )}
                  {!!result.intakeSubmissionId && (
                    <Chip backgroundColor="var(--N200)">
                      <PendingIcon height="0.8rem" /> Request
                    </Chip>
                  )}
                  {!!result.serviceId && (
                    <Chip backgroundColor="var(--N200)">
                      <ServicesIcon height="0.8rem" /> Service
                    </Chip>
                  )}
                  <Link to={pathFor(result)} onClick={onClose}>
                    {result.name}
                  </Link>
                </div>
              ))}
            </Box>
          )}
        </div>
      </StyleRoot>
    </Modal>
  );
};
